import type { EarthlyBranch } from '../types/branches';
import type { Ohaeng } from '../types/elements';
import type { Relation, RelationOptions } from '../types/relations';
import type { PillarPosition } from '../types/pillars';
import { branchIndex } from '../core/sexagenary';
import { isAdjacentPillar } from './adjacent';

// 육합 (Six Harmonies): pairs that combine
export const SIX_COMBINES: [number, number, Ohaeng | null][] = [
  [0, 1, '土'],    // 子丑合土
  [2, 11, '木'],   // 寅亥合木
  [3, 10, '火'],   // 卯戌合火
  [4, 9, '金'],    // 辰酉合金
  [5, 8, '水'],    // 巳申合水
  [6, 7, null],    // 午未합 (변화없음)
];

// 삼합 (Three Harmonies): three branches that combine into an element
export const THREE_COMBINES: [number, number, number, Ohaeng][] = [
  [8, 0, 4, '水'],   // 申子辰 합水
  [2, 6, 10, '火'],  // 寅午戌 합火
  [5, 9, 1, '金'],   // 巳酉丑 합금
  [11, 3, 7, '木'],  // 亥卯未 합목
];

// 방합 (Directional Harmonies): seasonal groupings
export const DIRECTIONAL_COMBINES: [number, number, number, Ohaeng][] = [
  [2, 3, 4, '木'],    // 寅卯辰 방합 (동방 木)
  [5, 6, 7, '火'],    // 巳午未 방합 (남방 火)
  [8, 9, 10, '金'],   // 申酉戌 방합 (서방 金)
  [11, 0, 1, '水'],   // 亥子丑 방합 (북방 水)
];

// 암합 (Hidden Harmonies): 정기(본기) 천간합 기반, 육합 쌍 제외
export const HIDDEN_COMBINES: [number, number, Ohaeng][] = [
  [1, 2, '土'],   // 丑寅 (己甲합 → 土)
  [2, 7, '土'],   // 寅未 (甲己합 → 土)
  [3, 8, '金'],   // 卯申 (乙庚합 → 金)
  [5, 9, '水'],   // 巳酉 (丙辛합 → 水)
  [6, 11, '木'],  // 午亥 (丁壬합 → 木)
  [0, 4, '火'],   // 子辰 (癸戊합 → 火)
  [0, 10, '火'],  // 子戌 (癸戊합 → 火)
];

export function findEarthlyCombines(
  branches: { branch: EarthlyBranch; position: PillarPosition }[],
  _monthBranch?: EarthlyBranch,
  options?: RelationOptions,
): Relation[] {
  const requireAdj = options?.requireAdjacency !== false;
  const blockContested = options?.blockContested !== false;
  const requireMonth = options?.requireMonthSupport !== false;

  const relations: Relation[] = [];
  const branchIndices = branches.map(b => ({ idx: branchIndex(b.branch), ...b }));

  // Check 육합 (pairs)
  const yukHapRelations: Relation[] = [];
  for (let i = 0; i < branchIndices.length; i++) {
    for (let j = i + 1; j < branchIndices.length; j++) {
      // 육합 인접성 체크
      if (requireAdj && !isAdjacentPillar(branchIndices[i].position, branchIndices[j].position)) continue;

      const a = branchIndices[i].idx;
      const b = branchIndices[j].idx;

      for (const [s1, s2, result] of SIX_COMBINES) {
        if ((a === s1 && b === s2) || (a === s2 && b === s1)) {
          const [first, second] = a === s1 ? [branchIndices[i], branchIndices[j]] : [branchIndices[j], branchIndices[i]];

          // 교재 기준: 육합은 합화(合化)하지 않음 → 결합만 하고 원래 속성 유지
          // requireMonthSupport=true(기본): 육합은 항상 transformed=false
          // requireMonthSupport=false(레거시): 기존 동작처럼 transformed=true
          const transformed = !requireMonth;

          yukHapRelations.push({
            type: '육합',
            pillars: [branchIndices[i].position, branchIndices[j].position],
            branches: [first.branch, second.branch],
            ...(result ? { result, transformed } : {}),
            description: result
              ? transformed
                ? `${first.branch}${second.branch}합 → ${result}`
                : `${first.branch}${second.branch}합 (결합, 원래 속성 유지)`
              : `${first.branch}${second.branch}합 (변화없음)`,
          });
        }
      }
    }
  }

  // 육합 쟁합 차단: 같은 position이 2개 이상 합에 관여하면 해당 합 모두 제거
  let filteredYukHap = yukHapRelations;
  if (blockContested && yukHapRelations.length > 1) {
    const posCount = new Map<string, number>();
    for (const r of yukHapRelations) {
      for (const p of r.pillars) posCount.set(p, (posCount.get(p) || 0) + 1);
    }
    const contested = new Set([...posCount.entries()].filter(([, c]) => c > 1).map(([p]) => p));
    if (contested.size > 0) {
      filteredYukHap = yukHapRelations.filter(r => !r.pillars.some(p => contested.has(p)));
    }
  }
  relations.push(...filteredYukHap);

  // Check 삼합 (triples)
  if (branchIndices.length >= 3) {
    for (const [b1, b2, b3, result] of THREE_COMBINES) {
      const found1 = branchIndices.find(b => b.idx === b1);
      const found2 = branchIndices.find(b => b.idx === b2);
      const found3 = branchIndices.find(b => b.idx === b3);

      if (found1 && found2 && found3) {
        relations.push({
          type: '삼합',
          pillars: [found1.position, found2.position, found3.position],
          branches: [found1.branch, found2.branch, found3.branch],
          result,
          description: `${found1.branch}${found2.branch}${found3.branch} 삼합 → ${result}`,
        });
      }
    }
  }

  // Check 반합 (half-combination): 삼합 2글자 중 왕지(子午卯酉) 포함 시
  // THREE_COMBINES의 index 1이 항상 왕지: [申,子,辰], [寅,午,戌], [巳,酉,丑], [亥,卯,未]
  if (branchIndices.length >= 2) {
    for (const [b1, b2, b3, result] of THREE_COMBINES) {
      const found1 = branchIndices.find(b => b.idx === b1);
      const found2 = branchIndices.find(b => b.idx === b2); // 왕지
      const found3 = branchIndices.find(b => b.idx === b3);

      // 삼합 성립 시 반합 아님
      if (found1 && found2 && found3) continue;

      // 왕지(b2)가 반드시 포함되어야 함
      if (!found2) continue;

      // 왕지 + 다른 1글자
      const partner = found1 || found3;
      if (!partner) continue;

      const [first, second] = found1 ? [found1, found2] : [found2, found3!];
      relations.push({
        type: '반합',
        pillars: [first.position, second.position],
        branches: [first.branch, second.branch],
        result,
        description: `${first.branch}${second.branch} 반합 → ${result}`,
      });
    }
  }

  // Check 방합 (directional triples)
  if (branchIndices.length >= 3) {
    for (const [b1, b2, b3, result] of DIRECTIONAL_COMBINES) {
      const found1 = branchIndices.find(b => b.idx === b1);
      const found2 = branchIndices.find(b => b.idx === b2);
      const found3 = branchIndices.find(b => b.idx === b3);

      if (found1 && found2 && found3) {
        relations.push({
          type: '방합',
          pillars: [found1.position, found2.position, found3.position],
          branches: [found1.branch, found2.branch, found3.branch],
          result,
          description: `${found1.branch}${found2.branch}${found3.branch} 방합 → ${result}`,
        });
      }
    }
  }

  return relations;
}
